import { useEffect, useRef, useState } from "react";
import { getToken } from "../lib/auth";
import { ehEmbutido } from "../lib/sso";
import { dateTimeBR, n } from "../lib/format";
import { fN } from "./EstoqueUi";

/* Responsabilidade e validação (reunião 24/10/2026): indicador das pendências do departamento do usuário + as contestadas
   dele (mesma tabela do ERP). Validar/contestar é no ERP (sino 🔔 no topo) — aqui só o aviso. Embutido no ERP não aparece
   (o sino do ERP já está no topo). */
interface Item { id: number; tipo: string; ref: string; descricao: string; dep: string; fez: string; dhFez: string; prazo: string; vencida: boolean; situac: string }
interface Resumo { ativo: boolean; pendentes: number; vencidas: number; contestadas: number; departamentos: string[]; itens: Item[] }
// reunião 28/10/2026: estoque abaixo do mínimo no mesmo indicador (/api/alertas/estoque-minimo)
interface Minimo { ativo: boolean; total: number; itens: { codpro: number; nompro: string; unidade: string; disponivel: number; minimo: number; aviso: string; fonte: string }[] }

export function PendenciasBadge() {
  const [r, setR] = useState<Resumo | null>(null);
  const [m, setM] = useState<Minimo | null>(null);
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const embutido = ehEmbutido();

  useEffect(() => {
    if (embutido) return;
    let vivo = true;
    const carregar = async () => {
      try {
        const t = getToken();
        const resp = await fetch("/api/pendencias/resumo", { headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (!resp.ok) return;
        const j = (await resp.json()) as Resumo;
        if (vivo) setR(j);
        const rm = await fetch("/api/alertas/estoque-minimo", { headers: t ? { Authorization: `Bearer ${t}` } : {} });
        if (rm.ok && vivo) setM((await rm.json()) as Minimo);
      } catch {
        /* rede: tenta no próximo ciclo */
      }
    };
    carregar();
    const id = window.setInterval(() => { if (!document.hidden) carregar(); }, 60_000);
    return () => { vivo = false; window.clearInterval(id); };
  }, [embutido]);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", fora); document.removeEventListener("keydown", esc); };
  }, [aberto]);

  const nMin = m?.ativo ? m.total : 0;
  if (embutido || ((!r || !r.ativo) && !nMin)) return null;
  const rr: Resumo = r && r.ativo ? r : { ativo: false, pendentes: 0, vencidas: 0, contestadas: 0, departamentos: [], itens: [] };
  const total = rr.pendentes + rr.contestadas + nMin;
  const cor = rr.vencidas ? "#dc2626" : total ? "#FF7200" : "rgba(255,255,255,0.18)";
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => setAberto((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAberto((v) => !v); } }}
        title={total ? `${total} validação(ões) aguardando${rr.vencidas ? ` — ${rr.vencidas} vencida(s)` : ""}` : "Validações — nada pendente"}
        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 16, background: cor, color: "#fff", fontWeight: 700, fontSize: 13, userSelect: "none" }}
      >
        🔔 {n(total)}
        {rr.vencidas ? <small style={{ fontWeight: 600 }}>· {n(rr.vencidas)} vencida(s)</small> : null}
      </span>
      {aberto && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 400, maxWidth: "92vw", maxHeight: "60vh", overflow: "auto", zIndex: 1000, background: "var(--card, #fff)", color: "var(--ink, #1f2430)", border: "1px solid var(--line, #e3e8f0)", borderRadius: 12, boxShadow: "0 12px 32px rgba(15,23,42,.22)", padding: 12, textAlign: "left" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Validações — {rr.departamentos.join(", ") || "sem departamento"}</div>
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>Validar / contestar pelo sino 🔔 no topo do ERP.</div>
          {rr.itens.length === 0 && <div style={{ padding: "18px 0", textAlign: "center", opacity: 0.7 }}>✅ Nada aguardando validação.</div>}
          {rr.itens.map((i) => (
            <div key={i.id} style={{ borderLeft: `4px solid ${i.situac === "CONTESTADA" ? "#7c3aed" : i.vencida ? "#dc2626" : "#FF7200"}`, padding: "6px 10px", marginBottom: 6, borderRadius: 6, background: i.vencida ? "rgba(220,38,38,.07)" : "transparent" }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{i.tipo} · Lote {i.ref} <span style={{ fontWeight: 400, opacity: 0.7 }}>({i.dep})</span>{i.situac === "CONTESTADA" ? " — CONTESTADA" : ""}</div>
              <div style={{ fontSize: 12.5 }}>{i.descricao}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {i.fez} em {dateTimeBR(i.dhFez)} · prazo <span style={i.vencida ? { color: "#dc2626", fontWeight: 700 } : undefined}>{dateTimeBR(i.prazo)}{i.vencida ? " — VENCIDA" : ""}</span>
              </div>
            </div>
          ))}
          {nMin ? (<>
            <div style={{ fontWeight: 700, margin: "10px 0 6px" }}>📉 Estoque abaixo do mínimo ({n(nMin)})</div>
            {m!.itens.map((x) => (
              <div key={x.codpro} style={{ borderLeft: `4px solid ${x.aviso === "VERMELHO" ? "#dc2626" : "#f59e0b"}`, padding: "6px 10px", marginBottom: 6, borderRadius: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{x.codpro} · {x.nompro}</div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>Disponível: <b>{fN(x.disponivel, x.unidade.trim().toUpperCase() === "KG" ? 2 : 0)} {x.unidade}</b> · {x.fonte === "LIMITE" ? "limite" : "mínimo"}: {fN(x.minimo, x.unidade.trim().toUpperCase() === "KG" ? 2 : 0)}</div>
              </div>
            ))}
          </>) : null}
        </div>
      )}
    </span>
  );
}
